import { Temporal } from "@js-temporal/polyfill";
import { z } from "zod";
import { dateStr, timeStr } from "./common";

/** Named zones only: a company timezone must retain its daylight-saving rules. */
export const scheduleTimezone = z.string().min(1).max(100).refine((value) => {
  if (/^[+-]/.test(value)) return false;
  try { new Temporal.ZonedDateTime(0n, value); return true; } catch { return false; }
}, "Choose a valid IANA timezone");
export const localPoint = z.object({
  date: dateStr.nullable(),
  time: timeStr.nullable(),
  offset: z.string().regex(/^[+-](?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/).nullable().optional(),
}).strict();
export type LocalPoint = z.infer<typeof localPoint>;
export const accessChoice = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("unknown") }).strict(),
  z.object({ mode: z.literal("none") }).strict(),
  z.object({ mode: z.literal("relative"), minutes: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER) }).strict(),
  z.object({ mode: z.literal("custom"), point: localPoint }).strict(),
]);
export type AccessChoice = z.infer<typeof accessChoice>;
export const reservation = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("timed") }).strict(),
  z.object({ mode: z.literal("all_day"), startDate: dateStr, endDate: dateStr }).strict(),
  z.object({
    mode: z.literal("legacy_unresolved"),
    startDate: dateStr.nullable(), endDate: dateStr.nullable(), unknownBounds: z.boolean(),
    reason: z.string().min(1).max(4000), raw: z.record(z.string(), z.string().nullable()).optional(),
  }).strict(),
]);
export type Reservation = z.infer<typeof reservation>;
export const scheduleV1 = z.object({
  version: z.literal(1), timezone: scheduleTimezone, eventStart: localPoint, eventEnd: localPoint,
  accessStart: accessChoice, accessEnd: accessChoice, reservation,
}).strict();
export type ScheduleV1 = z.infer<typeof scheduleV1>;
